import { F0Select, F0SelectItemObject, useF0Dialog } from '@factorialco/f0-react'
import {
  ProjectForSelectFragment,
  ProjectSelectorDocument,
  ProjectSelectorQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

export interface F0ProjectSelectorProps {
  value: ProjectForSelectFragment[]
  onChange: (projects: ProjectForSelectFragment[]) => void
  label?: string
  placeholder?: string
  mandatory?: boolean
  disabled?: boolean
  error?: string
  assignedEmployeeIds?: string[]
  notClosed?: boolean
  withoutClient?: boolean
  skipViewerByEmployeeId?: string
}

const getProjectLabel = (project: ProjectForSelectFragment): string => {
  return project.code ? `${project.name} (${project.code})` : project.name
}

export const F0ProjectSelector = ({
  value,
  onChange,
  label = i18n.t('project_management.modals.add_project.select_project'),
  placeholder = i18n.t('common.select_multiple'),
  mandatory,
  disabled,
  error,
  assignedEmployeeIds,
  notClosed,
  withoutClient = false,
  skipViewerByEmployeeId,
}: F0ProjectSelectorProps) => {
  const { portalContainer } = useF0Dialog()

  const source = useGraphqlDataSource(
    {
      document: ProjectSelectorDocument,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({
        search: search ?? undefined,
        assignedEmployeeIds: assignedEmployeeIds ?? undefined,
        notClosed: notClosed ?? undefined,
        skipViewerByEmployeeId: skipViewerByEmployeeId ?? undefined,
        withoutClient,
      }),
      mapData: (data: ProjectSelectorQuery) =>
        data.projectManagement?.projectsConnection?.edges?.map((edge) => edge.node) ?? [],
      mapPagination: (data: ProjectSelectorQuery) => {
        const connection = data.projectManagement?.projectsConnection
        const edges = connection?.edges ?? []
        const pageInfo = connection?.pageInfo
        const hasNextPage = pageInfo?.hasNextPage ?? false
        return {
          total: hasNextPage ? edges.length + 1 : edges.length,
          cursor: pageInfo?.endCursor ?? undefined,
          hasNextPage,
        }
      },
    },
    [assignedEmployeeIds, notClosed, withoutClient, skipViewerByEmployeeId]
  )

  const mapOptions = (
    project: ProjectForSelectFragment
  ): F0SelectItemObject<string, ProjectForSelectFragment> => ({
    value: String(project.id),
    label: getProjectLabel(project),
    item: project,
  })

  return (
    <F0Select<string, ProjectForSelectFragment>
      multiple
      clearable
      size="md"
      label={label}
      placeholder={placeholder}
      source={source}
      mapOptions={mapOptions}
      value={value.map((p) => String(p.id))}
      onChange={(_ids: string[], originalItems: ProjectForSelectFragment[]) => {
        onChange(originalItems)
      }}
      required={mandatory}
      disabled={disabled}
      error={error}
      showSearchBox
      portalContainer={portalContainer}
    />
  )
}
